import i18n from "i18next";
import { bidiIsolate } from "../../Helpers/formating";

// Params that carry a catalog category/pricelist name, per notification id.
// The API sends those names in English (the translation-JSON key), so they are
// translated here, at display time, with the same namespaces the dashboard
// uses — "Electrical Work" → "עבודות חשמל". Payloads stay untouched.
const CATEGORY_PARAMS = {
  2: ["p1", "p2"],
  6: ["p3"],
  18: ["p2"],
  19: ["p2"],
  20: ["p3"],
  30: ["p5"],
  31: ["p2", "p4"],
};
const CATEGORY_NAMESPACES = [
  "categories",
  "products_categories",
  "restaurants_categories",
];
const CATEGORY_LOOKUP = {
  ns: CATEGORY_NAMESPACES,
  nsSeparator: false,
  keySeparator: false,
};

const localizeCategoryName = (name) => {
  if (!name || typeof name !== "string") return name;
  const translate = (part) =>
    i18n.exists(part, CATEGORY_LOOKUP) ? i18n.t(part, CATEGORY_LOOKUP) : part;
  if (i18n.exists(name, CATEGORY_LOOKUP)) return translate(name);
  // Several categories joined into one param: "TVs & Home Theater, Garden & Patio".
  return name
    .split(",")
    .map((part) => translate(part.trim()))
    .join(", ");
};

/**
 * Interpolates DB template placeholders (#p1..#p5) with the recipient's params.
 * Used as a fallback when the i18n template for a notification id is missing.
 */
export function interpolateNotificationTemplate(template, params) {
  let result = template ?? "";
  // DB templates carry "#pN"; the generated i18n JSON uses "{pN}". The
  // fallback path can receive either, so both forms map to the same value.
  const paramMap = {
    "#p1": params.param1,
    "{p1}": params.param1,
    "#p2": params.param2,
    "{p2}": params.param2,
    "#p3": params.param3,
    "{p3}": params.param3,
    "#p4": params.param4,
    "{p4}": params.param4,
    "#p5": params.param5,
    "{p5}": params.param5,
  };
  for (const [placeholder, value] of Object.entries(paramMap)) {
    if (value != null) {
      // split/join instead of replaceAll — not polyfilled under CRA's browserslist
      result = result.split(placeholder).join(value);
    }
  }
  return result;
}

/**
 * Returns the { title, content, cta } for a notification recipient.
 * Primary source: the `notifications` i18n namespace (templates.{id}.* with
 * {pN} interpolation) so copy follows the app language. Falls back to the
 * raw DB template strings when the i18n key doesn't exist.
 */
export function getInterpolatedNotification(notification, t) {
  const { notifications, param1, param2, param3, param4, param5 } =
    notification;
  const safeNotifications = notifications ?? {
    id: "",
    title: "",
    content: "",
    cta: "",
  };
  const notificationId = String(safeNotifications.id);
  const params = { param1, param2, param3, param4, param5 };
  const rawParams = {
    p1: param1 ?? "",
    p2: param2 ?? "",
    p3: param3 ?? "",
    p4: param4 ?? "",
    p5: param5 ?? "",
  };
  const categoryParams = CATEGORY_PARAMS[notificationId] || [];
  // Category names follow the UI language; every value is isolated (<bdi>
  // equivalent) so English names and numbers don't break Hebrew sentences.
  const tParams = Object.fromEntries(
    Object.entries(rawParams).map(([key, value]) => [
      key,
      bidiIsolate(
        categoryParams.includes(key) ? localizeCategoryName(value) : value,
      ),
    ]),
  );
  // The generated templates use single braces ({pN}); i18next defaults to
  // double. Overridden per call so the other namespaces, which do use {{...}},
  // are unaffected — as is i18next's own loadPath.
  const tOptions = {
    ...tParams,
    interpolation: { prefix: "{", suffix: "}" },
  };

  if (t && i18n.exists(`notifications:templates.${notificationId}.title`)) {
    const cta = i18n.exists(`notifications:templates.${notificationId}.cta`)
      ? t(`templates.${notificationId}.cta`, tOptions)
      : null;
    const cta2 = i18n.exists(`notifications:templates.${notificationId}.cta2`)
      ? t(`templates.${notificationId}.cta2`, tOptions)
      : null;
    return {
      title: t(`templates.${notificationId}.title`, tOptions),
      content: t(`templates.${notificationId}.content`, tOptions),
      cta: cta || null,
      cta2: cta2 || null,
    };
  }

  return {
    title: interpolateNotificationTemplate(safeNotifications.title, params),
    content: interpolateNotificationTemplate(safeNotifications.content, params),
    cta: safeNotifications.cta
      ? interpolateNotificationTemplate(safeNotifications.cta, params)
      : null,
    cta2: null,
  };
}
